import axios from "axios";
import { useCookies } from "vue3-cookies";
import { useOrgStore } from "@/stores/org";
import { useToastStore } from "@/stores/toast";

// Opt-out flag for the global error toast below. Set it per-request on any
// call whose rejection the calling view already surfaces itself, so the two
// don't double-show:
//   apiClient.post(url, body, { skipGlobalErrorToast: true })
declare module "axios" {
  export interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean;
  }
}

const { cookies } = useCookies();
const baseURL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const instance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

instance.interceptors.request.use((request: any) => {
  const token = cookies.get("Convena-AccessToken");
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  // Multi-tenancy: attach the active organization id when one is selected.
  // useOrgStore() is called inside the interceptor (not at module top
  // level) so Pinia is guaranteed to be installed by the time a request
  // actually fires. The try/catch covers the edge case of a request
  // happening before app.use(createPinia()) — header is just omitted then.
  try {
    const orgStore = useOrgStore();
    if (orgStore.currentOrg) {
      request.headers["X-Organization-ID"] = orgStore.currentOrg.id;
    }
  } catch (e) {
    // Pinia not yet active — skip header. Endpoints that require it will
    // reject with 400, which the caller can handle.
  }

  if (request.data instanceof FormData) {
    delete request.headers["Content-Type"];
  }

  return request;
});

// Best-effort message extraction. Unlike a view handling one known endpoint,
// this runs against every response in the app, so it has to cope with each
// shape the backend actually uses: a top-level `message` (the billing/quota
// gates), a Laravel `errors` bag, and the `error` bag that some of the older
// participant endpoints return.
const extractMessage = (error: any): string => {
  if (!error.response) {
    return "Tidak dapat terhubung ke server. Periksa koneksi internet Anda.";
  }

  const data = error.response.data;

  if (typeof data === "string" && data.trim()) return data.trim();

  // `message: "Error"` is a placeholder the validation responses send
  // alongside the real per-field text — not worth showing on its own.
  if (data?.message && data.message !== "Error") return data.message;

  const bag = data?.errors ?? data?.error;
  if (bag) {
    const flat = Object.values(bag).flat(Infinity).filter(Boolean);
    if (flat.length) return flat.join(" ");
  }

  if (error.response.status >= 500) {
    return "Terjadi kesalahan pada server. Silakan coba lagi.";
  }
  return "Permintaan gagal diproses. Silakan coba lagi.";
};

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      cookies.remove("Convena-AccessToken");
      window.location.href = "/login";
      // No toast — the page is already navigating away to the login screen.
      return Promise.reject(error);
    }

    // Views that render their own error state opt out per-request. Everything
    // else gets a toast instead of failing silently, which was the default
    // for ~60 of this app's call sites.
    if (!error.config?.skipGlobalErrorToast) {
      try {
        useToastStore().push(extractMessage(error));
      } catch (e) {
        // Pinia not active yet (a request firing before app.use(createPinia())).
        // Nothing to show it in — fall through to the rejection.
      }
    }

    // Always re-reject: every existing .catch() in the app keeps working
    // exactly as before. This interceptor is purely additive.
    return Promise.reject(error);
  },
);

export default instance;
